import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import TextField from '../../Form/TextField/TextField';
import CustomCalendarHeader from '../../Form/CalendarInput/CustomCalendarHeader';
import CalendarInput from '../../Form/CalendarInput/CalendarInput';
import { deleteModalConfigs, maxMobileSize } from '../../../../utils/constants';
import { useWindowSize } from '../../../../utils/hooks';
import ValidationErrorLabel from '../../ValidationErrorLabel/ValidationErrorLabel';
import { BinOutlinedIcon } from '../../Icons';
import DeleteDialog from '../DeleteDialog';
import classes from '../scss/addEditFormModal.module.scss';

import('react-datepicker/dist/react-datepicker.css');
import('../../../../scss/datepicker.css');

function WorksForm(props) {
  const { addEditForm, withCurrentlyCheckBox, deleteHandler } = props;

  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const deleteModalConfig = useMemo(() => deleteModalConfigs(t), [t]);

  const changeCurrentlyCheckbox = (e) => {
    addEditForm.setFieldValue('enddate', new Date());
    addEditForm.setFieldValue('currentlyWorkThere', e.target.checked);
  };

  const size = useWindowSize();

  return (
    <div className={classes.formContainer}>
      <div className={`col-md-3 ${classes.textField}`}>
        <TextField
          id="title"
          name="title"
          placeholder="Job Title"
          value={addEditForm.values.title}
          onChange={addEditForm.handleChange}
          error={addEditForm.touched.title && Boolean(addEditForm.errors.title)}
          errorText={addEditForm.touched.title && addEditForm.errors.title}
          clearable
        />
        {addEditForm.values.title && (
          <div
            data-testid="clearBtn"
            role="presentation"
            className={classes.closeIcon}
            onClick={() => addEditForm.setFieldValue('title', '', true)}
          >
            <CloseIcon fontSize="8px" />
          </div>
        )}
      </div>
      <div className={`col-md-3 ${classes.textField}`}>
        <TextField
          id="company"
          name="company"
          placeholder="Company Name"
          value={addEditForm.values.company}
          onChange={addEditForm.handleChange}
          error={addEditForm.touched.company && Boolean(addEditForm.errors.company)}
          errorText={addEditForm.touched.company && addEditForm.errors.company}
          clearable
        />
        {addEditForm.values.company && (
          <div
            data-testid="clearBtn"
            role="presentation"
            className={classes.closeIcon}
            onClick={() => addEditForm.setFieldValue('company', '', true)}
          >
            <CloseIcon fontSize="8px" />
          </div>
        )}
      </div>
      <div className="col-md-4 d-flex flex-row justify-content-start align-items-end gap-2">
        <DatePicker
          disabledKeyboardNavigation
          className={classes.picker}
          withPortal={size.width < maxMobileSize}
          portalId="root-portal"
          selected={addEditForm.values.startdate}
          name="startdate"
          data-testid="startDate"
          dateFormat="dd MMM yyyy"
          renderCustomHeader={
            // eslint-disable-next-line react/jsx-props-no-spreading,no-shadow
            (props) => <CustomCalendarHeader {...props} />
          }
          onChange={(e) => addEditForm.setFieldValue('startdate', e, true)}
          customInput={
            <CalendarInput
              label="Start Date"
              touched={addEditForm.touched.startdate}
              errors={addEditForm.errors.startdate}
            />
          }
        />
        <DatePicker
          disabledKeyboardNavigation
          disabled={withCurrentlyCheckBox && addEditForm.values.currentlyWorkThere}
          withPortal={size.width < maxMobileSize}
          portalId="root-portal"
          selected={addEditForm.values.enddate}
          name="enddate"
          data-testid="endDate"
          dateFormat="dd MMM yyyy"
          renderCustomHeader={
            // eslint-disable-next-line react/jsx-props-no-spreading,no-shadow
            (props) => <CustomCalendarHeader {...props} />
          }
          onChange={(e) => addEditForm.setFieldValue('enddate', e, true)}
          customInput={
            <CalendarInput
              disabled={withCurrentlyCheckBox && addEditForm.values.currentlyWorkThere}
              label="End Date"
              touched={addEditForm.touched.enddate}
              errors={addEditForm.errors.enddate}
            />
          }
        />
        {withCurrentlyCheckBox && (
          <div className={classes.currentCheckBox}>
            <Checkbox
              icon={<div className={classes.customCheck} />}
              checkedIcon={
                <div className={classes.customCheck}>
                  <DoneIcon />
                </div>
              }
              onClick={changeCurrentlyCheckbox}
              checked={addEditForm.values.currentlyWorkThere}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 15 }, padding: 0 }}
            />
            <span className={classes.checkBoxLabel}>I Currently Work Here</span>
          </div>
        )}
      </div>
      {addEditForm.touched.currentlyWorkThere && addEditForm.errors.currentlyWorkThere && (
        <ValidationErrorLabel message={addEditForm.errors.currentlyWorkThere} />
      )}
      <div className={`${classes.description} ${classes.textField}`}>
        <TextField
          isTextArea
          rows="3"
          variant="standard"
          id="description"
          name="description"
          placeholder="Description"
          value={addEditForm.values.description}
          onChange={(e) => {
            addEditForm.setFieldTouched('description', true, true);
            addEditForm.handleChange(e);
          }}
          error={addEditForm.touched.description && Boolean(addEditForm.errors.description)}
          errorText={addEditForm.touched.description && addEditForm.errors.description}
          clearable
        />
        {addEditForm.values.description && (
          <div
            data-testid="clearBtn"
            role="presentation"
            className={classes.closeIcon}
            onClick={() => addEditForm.setFieldValue('description', '', true)}
          >
            <CloseIcon fontSize="8px" />
          </div>
        )}
      </div>
      {!!deleteHandler && (
        <div className={classes.deleteBtn} role="presentation" onClick={() => setOpen(true)}>
          <span>{t('common.general.delete')}</span> <BinOutlinedIcon />
        </div>
      )}
      <DeleteDialog
        open={open}
        closeHandler={() => setOpen(false)}
        confirmHandler={deleteHandler}
        config={deleteModalConfig.works}
      />
    </div>
  );
}

export default WorksForm;
